"use client"

import { cn } from "@/lib/utils"

import styles from "./shine-border.module.css"

/**
 * Magic UI "Shine Border" — an animated gradient border that travels around the
 * element. Source: https://magicui.design/docs/components/shine-border
 *
 * Local adaptation: the shine keyframes live in `shine-border.module.css`
 * instead of a global `animate-shine` Tailwind utility, so this drops in without
 * editing the (protected) tailwind config. Animation is gated on
 * `prefers-reduced-motion: no-preference`.
 *
 * Render inside a `position: relative` element with a matching border radius.
 */
interface ShineBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width of the border in pixels. */
  borderWidth?: number
  /** Animation duration in seconds. */
  duration?: number
  /** Border colour — a single colour (monotone) or an array for a gradient. */
  shineColor?: string | string[]
}

export function ShineBorder({
  borderWidth = 1,
  duration = 14,
  shineColor = "#000000",
  className,
  style,
  ...props
}: ShineBorderProps) {
  return (
    <div
      style={
        {
          "--border-width": `${borderWidth}px`,
          "--duration": `${duration}s`,
          backgroundImage: `radial-gradient(transparent,transparent, ${
            Array.isArray(shineColor) ? shineColor.join(",") : shineColor
          },transparent,transparent)`,
          backgroundSize: "300% 300%",
          mask: `linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0)`,
          WebkitMask: `linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0)`,
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "var(--border-width)",
          ...style,
        } as React.CSSProperties
      }
      className={cn(
        "pointer-events-none absolute inset-0 size-full rounded-[inherit] will-change-[background-position]",
        styles.shine,
        className,
      )}
      {...props}
    />
  )
}
